// src/components/visits/VisitDetailsView.jsx
import React, { useMemo } from 'react';
import {
  ArrowLeft,
  MapPin,
  Image as ImageIcon,
  Video,
  Download,
  Mail,
  Loader2,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { useTheme } from '../../theme/ThemeContext';

// IMPORTANT: make sure leaflet CSS is bundled
import 'leaflet/dist/leaflet.css';

/* ------------------------------------------------------------------ */
/*  FILE URL HELPERS                                                  */
/* ------------------------------------------------------------------ */

const FILE_BASE_URL = 'https://files.patroll-management.nissiindia.com';

const cleanUrlCandidate = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (!s || s === '-' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') {
    return '';
  }
  return s;
};

const buildMediaUrl = (raw) => {
  const candidate = cleanUrlCandidate(raw);
  if (!candidate) return '';

  if (/^https?:\/\//i.test(candidate)) return candidate;

  const base = FILE_BASE_URL.replace(/\/+$/, '');
  const path = candidate.replace(/^\/+/, '');
  return `${base}/${path}`;
};

/* ------------------------------------------------------------------ */
/*  MAP / BADGES                                                      */
/* ------------------------------------------------------------------ */

// Use a divIcon instead of external PNG files to avoid broken-image issues in prod
const defaultMarkerIcon = L.divIcon({
  className: 'custom-visit-marker',
  html:
    '<div style="width:14px;height:14px;border-radius:9999px;background:#0ea5e9;border:2px solid white;box-shadow:0 0 4px rgba(0,0,0,0.4)"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 16],
});

const badgeBase =
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium';

const statusBadgeClass = (status) => {
  if (!status)
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  switch (status) {
    case 'captured':
    case 'uploading':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'uploaded':
      return 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300';
    case 'delivered':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    default:
      return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  }
};

const approveBadgeClass = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'approved':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'rejected':
      return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
    default:
      return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  }
};

const modeBadgeClass = (mode) => {
  if (!mode) {
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  }
  const v = String(mode).toLowerCase();
  if (v === 'manual') {
    return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';
  }
  if (v === 'auto' || v === 'automatic') {
    return 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300';
  }
  return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
};

/* ------------------------------------------------------------------ */
/*  SUPER-ROBUST UTC → IST HELPERS                                    */
/* ------------------------------------------------------------------ */

const toIstDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  const utcMs = d.getTime();
  const istOffsetMs = 5.5 * 60 * 60 * 1000; // +05:30
  return new Date(utcMs + istOffsetMs);
};

const formatDateTime = (value) => {
  const d = toIstDate(value);
  if (!d) {
    const s = value ? String(value).trim() : '';
    return s || '-';
  }

  const date = d.toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const time = d.toLocaleTimeString('en-IN', {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${date}, ${time}`;
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const s = String(value).trim();
    return s || '-';
  }

  return d.toLocaleDateString('en-IN', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                         */
/* ------------------------------------------------------------------ */

const VisitDetailsView = ({ visit, loading, onBack, onSendMail, sendingMail }) => {
  const { theme } = useTheme();

  const photos = useMemo(
    () => (visit?.media || []).filter((m) => m.media_type === 'photo'),
    [visit?.media]
  );
  const videos = useMemo(
    () => (visit?.media || []).filter((m) => m.media_type === 'video'),
    [visit?.media]
  );

  // ✅ NORMALISE CHECKLIST (from array or JSON string)
  const checklistItems = useMemo(() => {
    if (!visit) return [];

    if (Array.isArray(visit.checklist_answers) && visit.checklist_answers.length) {
      return visit.checklist_answers;
    }

    if (visit.checklist_answers_json) {
      try {
        const parsed = JSON.parse(visit.checklist_answers_json);
        if (Array.isArray(parsed)) return parsed;
      } catch (err) {
        console.error('Failed to parse checklist_answers_json', err);
      }
    }

    return [];
  }, [visit]);

  // ---- Location handling (fallbacks) -------------------------------
  const { gps_lat, gps_lng } = visit || {};
  const {
    gps_latitude,
    gps_longitude,
    property_lat,
    property_lng,
    property_latitude,
    property_longitude,
    lat: plainLat,
    lng: plainLng,
    latitude,
    longitude,
  } = visit || {};

  const lat = useMemo(() => {
    const v =
      gps_lat ??
      gps_latitude ??
      plainLat ??
      latitude ??
      property_lat ??
      property_latitude;
    if (v == null) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }, [
    gps_lat,
    gps_latitude,
    plainLat,
    latitude,
    property_lat,
    property_latitude,
  ]);

  const lng = useMemo(() => {
    const v =
      gps_lng ??
      gps_longitude ??
      plainLng ??
      longitude ??
      property_lng ??
      property_longitude;
    if (v == null) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }, [
    gps_lng,
    gps_longitude,
    plainLng,
    longitude,
    property_lng,
    property_longitude,
  ]);

  const hasLocation =
    lat !== null && lng !== null && !Number.isNaN(lat) && !Number.isNaN(lng);

  console.debug('[VisitDetailsView] visit location fields', {
    gps_lat,
    gps_lng,
    gps_latitude,
    gps_longitude,
    property_lat,
    property_lng,
    lat,
    lng,
  });

  const mode = visit?.mode || visit?.mode_type || visit?.run_type || null;
  const isManualMode =
    String(mode || '').toLowerCase() === 'manual' ||
    String(visit?.run_type || '').toLowerCase() === 'manual';

  const handleSendVisitMail = () => {
    if (onSendMail && visit) {
      onSendMail(visit);
    }
  };

  if (loading || !visit) {
    return <div className="text-sm text-slate-500">Loading visit…</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:border-sky-300 hover:text-sky-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-sky-500 dark:hover:text-sky-400"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to visits
        </button>

        <div className="flex flex-col gap-2 text-right">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Visit ID #{visit.visit_id}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <span className={`${badgeBase} ${statusBadgeClass(visit.status)}`}>
              Status: {visit.status || '-'}
            </span>
            <span
              className={`${badgeBase} ${approveBadgeClass(
                visit.approve_status
              )}`}
            >
              Approval: {visit.approve_status || '-'}
            </span>
            <span className={`${badgeBase} ${modeBadgeClass(mode)}`}>
              Mode: {mode || '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Main info */}
      <section
        className={`grid gap-4 rounded-xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 md:grid-cols-3 ${
          theme.card
        }`}
      >
        <div className="space-y-3 md:col-span-2">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
              {visit.client_name || 'Client'} • {visit.property_name || 'Property'}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <MapPin className="h-3.5 w-3.5" />
              <span>
                {visit.address_line1 || ''}
                {visit.address_line2 ? `, ${visit.address_line2}` : ''}
                {visit.city ? `, ${visit.city}` : ''}
                {visit.state ? `, ${visit.state}` : ''}
                {visit.pincode ? ` • ${visit.pincode}` : ''}
                {visit.country ? ` • ${visit.country}` : ''}
              </span>
            </div>
          </div>

          <div className="grid gap-3 text-xs text-slate-600 dark:text-slate-300 sm:grid-cols-3">
            <div>
              <div className="font-semibold text-slate-700 dark:text-slate-200">
                Guard
              </div>
              <div className="mt-0.5">{visit.guard_name || '-'}</div>
            </div>

            <div>
              <div className="font-semibold text-slate-700 dark:text-slate-200">
                Run
              </div>
              <div className="mt-0.5 text-ellipsis text-xs">
                {visit.run_title || '-'}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                {visit.run_type || 'unknown'}
                {mode && ` • ${mode}`}
                {' • '}
                {visit.run_date ? formatDateOnly(visit.run_date) : '-'}
              </div>
            </div>

            <div>
              <div className="font-semibold text-slate-700 dark:text-slate-200">
                Timeline (IST)
              </div>
              <div className="mt-0.5">
                <span className="text-[11px] text-slate-400">Captured: </span>
                {formatDateTime(visit.captured_at)}
              </div>
              <div className="mt-0.5">
                <span className="text-[11px] text-slate-400">Uploaded: </span>
                {formatDateTime(visit.uploaded_at)}
              </div>
              <div className="mt-0.5">
                <span className="text-[11px] text-slate-400">Delivered: </span>
                {formatDateTime(visit.delivered_at)}
              </div>
            </div>
          </div>
        </div>

        {/* Map preview */}
        <div className="md:col-span-1">
          <div className="mb-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              <span>Location</span>
            </div>
            {visit.gps_accuracy_m != null && (
              <span>Accuracy: ~{visit.gps_accuracy_m} m</span>
            )}
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
            {hasLocation ? (
              <MapContainer
                center={[lat, lng]}
                zoom={17}
                style={{ height: 220, width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution="&copy; OpenStreetMap contributors"
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[lat, lng]} icon={defaultMarkerIcon} />
              </MapContainer>
            ) : (
              <div className="flex h-[220px] items-center justify-center text-xs text-slate-400">
                Location not available.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Manual mode – Review + Send Mail */}
      {isManualMode && (
        <section className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
            <div className="mb-3 flex items-center justify-between text-xs">
              <div className="font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Review (Manual Visit)
              </div>
            </div>

            <div className="space-y-2 text-xs text-slate-700 dark:text-slate-200">
              <div>
                <div className="font-medium">Review Content</div>
                <div className="mt-0.5 text-slate-600 dark:text-slate-300">
                  {visit.review_comment || visit.review_notes || (
                    <span className="text-slate-400">
                      No review content available.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-between rounded-xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Actions
            </div>
            <div className="space-y-2 text-xs text-slate-600 dark:text-slate-300">
              <p>
                Send this manual visit report by email. The email includes property
                details, visited guard details, and all media files as a ZIP
                attachment/link (handled in backend).
              </p>
            </div>
            <div className="mt-3">
              <button
                type="button"
                disabled={sendingMail || !onSendMail}
                onClick={handleSendVisitMail}
                className="inline-flex w-full items-center justify-center rounded-full bg-sky-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sendingMail ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Sending mail…
                  </>
                ) : (
                  <>
                    <Mail className="mr-1.5 h-3.5 w-3.5" />
                    Send Mail
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Media grid (NO inline preview, only counts + download buttons) */}
      <section className="grid gap-4 md:grid-cols-3">
        {/* Photos */}
        <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
          <div className="mb-3 flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <ImageIcon className="h-4 w-4" />
              <span>Photos</span>
            </div>
            <span className="text-slate-400">
              {photos.length} photo{photos.length === 1 ? '' : 's'}
            </span>
          </div>

          {photos.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-xs text-slate-400">
              No photos for this visit.
            </div>
          ) : (
            <div className="space-y-2">
              {photos.map((photo, index) => {
                const fileCandidate =
                  cleanUrlCandidate(photo.file_url) ||
                  cleanUrlCandidate(photo.thumb_url);
                const fileUrl = buildMediaUrl(fileCandidate);

                return (
                  <div
                    key={photo.media_id || index}
                    className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs shadow-sm dark:border-slate-700 dark:bg-slate-900"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-700 dark:text-slate-200">
                        Photo {index + 1}
                      </span>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">
                        Uploaded:{' '}
                        {formatDateTime(photo.uploaded_at || visit.uploaded_at)}
                      </span>
                    </div>
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800"
                    >
                      <Download className="h-3 w-3" />
                      View / Download
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Videos */}
        <div className="rounded-xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
          <div className="mb-3 flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
              <Video className="h-4 w-4" />
              <span>Videos</span>
            </div>
            <span className="text-slate-400">
              {videos.length} video{videos.length === 1 ? '' : 's'}
            </span>
          </div>

          {videos.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-xs text-slate-400">
              No videos for this visit.
            </div>
          ) : (
            <div className="space-y-2">
              {videos.map((video, index) => {
                const videoCandidate = cleanUrlCandidate(video.file_url);
                const videoUrl = buildMediaUrl(videoCandidate);

                return (
                  <div
                    key={video.media_id || index}
                    className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-900 px-3 py-2 text-xs text-slate-200 shadow-sm dark:border-slate-700"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-100">
                        Video {index + 1}
                      </span>
                      <span className="text-[11px] text-slate-300">
                        Uploaded:{' '}
                        {formatDateTime(video.uploaded_at || visit.uploaded_at)}
                      </span>
                    </div>
                    <a
                      href={videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-slate-400 px-3 py-1 text-[11px] font-medium hover:bg-slate-800"
                    >
                      <Download className="h-3 w-3" />
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Checklist & notes */}
      <section className="grid gap-4 md:grid-cols-3">
        {/* ✅ Checklist – show only checklist name (item / question) */}
        <div className="rounded-xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 md:col-span-2">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Checklist Answers
          </div>

          {checklistItems.length === 0 ? (
            <div className="text-xs text-slate-400">
              No checklist data for this visit.
            </div>
          ) : (
            <ul className="space-y-1 text-xs text-slate-700 dark:text-slate-200">
              {checklistItems.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/70 px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900/60"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-500 dark:bg-sky-400" />
                  <span>
                    {item.item || item.question || `Item ${idx + 1}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Notes */}
        <div className="rounded-xl border border-slate-200 bg-white/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900/80">
          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Notes
          </div>
          <div className="text-xs text-slate-700 dark:text-slate-200">
            {visit.notes ? (
              <p className="whitespace-pre-line">{visit.notes}</p>
            ) : (
              <p className="text-slate-400">No notes added for this visit.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisitDetailsView;
